import { useMemo, useState } from 'react';
import { AlertCircle, AlertTriangle, Check, Sparkles } from 'lucide-react';
import { usePlans } from '@/hooks/usePlans';
import { useMonthlyUsageSummary, useSubscription } from '@/hooks/useBilling';
import UpgradePlanModal from '@/components/UpgradePlanModal';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { PageContainer } from '@/components/ui/page-container';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

interface Plan {
  id: number;
  name: string;
  slug: string;
  base_price_monthly: string;
  base_price_annual: string;
  devices_included: number;
  devices_limit: number;
  gateways_included: number;
  gateways_limit: number;
  users_included: number;
  users_limit: number;
  regions_included: number;
  regions_limit: number;
}

/**
 * Plans — plan-tier comparison page. Tailwind/shadcn rewrite of the
 * Bulma original. Behavior preserved verbatim:
 *
 *   • usePlans → cards of all tiers; usePlans loading → skeletons
 *   • Most-popular highlight on the `professional` tier (brand-outlined)
 *   • "Current plan" indicator when subscription.plan_id matches, OR
 *     when on free tier with no Stripe subscription
 *   • Upgrade / Change-plan button opens UpgradePlanModal which handles
 *     the SCA-aware Stripe payment flow (unchanged)
 *
 * The UpgradePlanModal is still a Bulma modal — it will migrate to
 * <AppSheet> when Billing's other surfaces migrate.
 */

const PLAN_DESCRIPTIONS: Record<string, string> = {
  free: 'Perfect for homelabs and small office networks',
  starter: 'Ideal for pilot projects and small businesses',
  professional: 'Multi-site networks and growing businesses',
  business: 'Enterprise-grade for complex networks',
  enterprise: 'Very large deployments with custom requirements',
};

function formatCurrency(amount: string): string {
  const num = parseFloat(amount);
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(num);
}

/**
 * Usage-vs-limit fit for a single plan attribute. The denominator is the
 * plan's hard limit: red (`over`) only once usage *exceeds* the cap — the
 * tier can't hold you — and yellow (`warn`) from past 80% up to and including
 * the cap (it still fits, but with little or no headroom). Using exactly the
 * limit (e.g. 1 of 1 regions) fits, so it's `warn`, not `over`. A limit of 0,
 * or absent usage data, is treated as "no signal" → `ok`.
 */
type FitStatus = 'ok' | 'warn' | 'over';

const WARN_THRESHOLD = 0.8;

function fitStatus(usage: number | undefined, limit: number): FitStatus {
  if (usage === undefined || limit <= 0) return 'ok';
  if (usage > limit) return 'over';
  if (usage > limit * WARN_THRESHOLD) return 'warn';
  return 'ok';
}

/** Join nouns into a readable list: "devices", "devices and regions", "a, b, and c". */
function formatNounList(nouns: string[]): string {
  if (nouns.length <= 1) return nouns.join('');
  if (nouns.length === 2) return nouns.join(' and ');
  return `${nouns.slice(0, -1).join(', ')}, and ${nouns.slice(-1).join('')}`;
}

export default function Plans() {
  const { data: plans, isLoading, error } = usePlans();
  const { data: subscription } = useSubscription();
  // Current usage drives the tier-fit indicators below: peak usage per
  // resource, compared against each plan's hard limits.
  const { data: usage } = useMonthlyUsageSummary();
  const usageByType = useMemo<Record<string, number | undefined>>(() => {
    const map: Record<string, number | undefined> = {};
    for (const r of usage?.resource_summaries ?? []) {
      map[r.resource_type.toLowerCase()] = r.peak_usage;
    }
    return map;
  }, [usage]);
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);

  const handleUpgradeClick = (plan: Plan) => {
    setSelectedPlan(plan);
    setIsUpgradeModalOpen(true);
  };
  const handleCloseModal = () => {
    setIsUpgradeModalOpen(false);
    setSelectedPlan(null);
  };

  if (error) {
    return (
      <PageContainer width="medium">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            <strong>Error loading plans:</strong> {error.message}
          </AlertDescription>
        </Alert>
      </PageContainer>
    );
  }

  return (
    <PageContainer width="medium">
      <header className="text-center mb-8 md:mb-10">
        <h1 className="text-2xl md:text-3xl font-semibold tracking-tight">Choose your plan</h1>
        <p className="text-sm md:text-base text-muted-foreground mt-2">
          Flexible plans that scale with your network
        </p>
      </header>

      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-96 rounded-lg" />
          ))}
        </div>
      ) : plans && plans.length > 0 ? (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {plans.map((plan) => {
              const isPopular = plan.slug === 'professional';
              const isFree = plan.slug === 'free';
              const isCurrent =
                subscription?.plan_id === plan.id ||
                (isFree && !subscription?.stripe_subscription_id);

              const attrs = [
                { noun: 'device', nounPlural: 'devices', included: plan.devices_included, limit: plan.devices_limit, usage: usageByType.devices },
                { noun: 'gateway', nounPlural: 'gateways', included: plan.gateways_included, limit: plan.gateways_limit, usage: usageByType.gateways },
                { noun: 'user', nounPlural: 'users', included: plan.users_included, limit: plan.users_limit, usage: usageByType.users },
                { noun: 'region', nounPlural: 'regions', included: plan.regions_included, limit: plan.regions_limit, usage: usageByType.regions },
              ];
              const fits = attrs.map((a) => fitStatus(a.usage, a.limit));
              const worstFit: FitStatus = fits.includes('over')
                ? 'over'
                : fits.includes('warn')
                  ? 'warn'
                  : 'ok';
              const overAttrs = attrs.filter((_, i) => fits[i] === 'over').map((a) => a.nounPlural);

              return (
                <article
                  key={plan.id}
                  className={cn(
                    'flex flex-col rounded-lg border bg-card overflow-hidden',
                    isPopular
                      ? 'border-2 border-primary shadow-[0_4px_24px_-8px_rgb(0_0_0/0.12)]'
                      : 'border-border',
                  )}
                >
                  {isPopular ? (
                    <div className="bg-primary text-primary-foreground text-center py-1.5 text-xs font-medium uppercase tracking-wider inline-flex items-center justify-center gap-1.5">
                      <Sparkles className="w-3.5 h-3.5" />
                      Most popular
                    </div>
                  ) : null}

                  {worstFit !== 'ok' ? (
                    <div
                      className={cn(
                        'flex items-center justify-center gap-1.5 px-4 py-1.5 text-xs font-medium',
                        worstFit === 'over' ? 'bg-sev-2-soft text-sev-2' : 'bg-sev-3-soft text-sev-3',
                      )}
                    >
                      {worstFit === 'over' ? (
                        <AlertCircle className="w-3.5 h-3.5" />
                      ) : (
                        <AlertTriangle className="w-3.5 h-3.5" />
                      )}
                      {worstFit === 'over' ? 'Too small for your usage' : 'Near your usage limits'}
                    </div>
                  ) : null}

                  <div className="p-6 flex-1 flex flex-col">
                    <div className="text-center">
                      <Badge variant="secondary" className="text-sm px-3 py-1">
                        {plan.name}
                      </Badge>
                      <div className="mt-4">
                        <span className="text-3xl font-semibold tracking-tight">
                          {formatCurrency(plan.base_price_monthly)}
                        </span>
                        <span className="text-sm text-muted-foreground ml-1">/month</span>
                      </div>
                      {!isFree ? (
                        <p className="text-xs text-muted-foreground mt-1">
                          or {formatCurrency(plan.base_price_annual)}/year
                        </p>
                      ) : null}
                      <p className="text-sm text-muted-foreground mt-4 min-h-[2.5em]">
                        {PLAN_DESCRIPTIONS[plan.slug] ?? ''}
                      </p>
                    </div>

                    <ul className="mt-6 space-y-2 flex-1 text-sm">
                      {attrs.map((a) => (
                        <PlanInclude
                          key={a.nounPlural}
                          included={a.included}
                          limit={a.limit}
                          noun={a.noun}
                          nounPlural={a.nounPlural}
                          showLimit={!isFree}
                          usage={a.usage}
                        />
                      ))}
                    </ul>

                    <div className="mt-6">
                      {isCurrent ? (
                        <Button variant="outline" className="w-full" disabled>
                          Current plan
                        </Button>
                      ) : !isFree && worstFit === 'over' ? (
                        // Usage exceeds this *paid* plan's hard limits — the
                        // backend rejects the change ("reduce usage first"), so
                        // block it up front instead of a dead-end click. Free is
                        // exempt: downgrading to Free is leaving/cancellation and
                        // must always be allowed regardless of usage.
                        <div className="space-y-1.5">
                          <Button variant="outline" className="w-full" disabled>
                            {subscription?.stripe_subscription_id ? 'Change to' : 'Upgrade to'}{' '}
                            {plan.name}
                          </Button>
                          <p className="text-xs text-sev-2 text-center">
                            Reduce {formatNounList(overAttrs)} to switch to this plan.
                          </p>
                        </div>
                      ) : (
                        <Button
                          className="w-full"
                          variant={isPopular ? 'default' : 'outline'}
                          onClick={() => { handleUpgradeClick(plan); }}
                        >
                          {subscription?.stripe_subscription_id ? 'Change to' : 'Upgrade to'}{' '}
                          {plan.name}
                        </Button>
                      )}
                    </div>
                  </div>
                </article>
              );
            })}
          </div>

          <section className="mt-10 bg-bg-subtle border border-border rounded-lg p-5 md:p-6">
            <h3 className="text-base font-semibold mb-3">How billing works</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <h4 className="text-sm font-medium mb-1.5">Included resources</h4>
                <p className="text-sm text-muted-foreground">
                  Your base plan price includes a set number of devices, gateways, users, and
                  regions. These are yours to use with no additional charges.
                </p>
              </div>
              <div>
                <h4 className="text-sm font-medium mb-1.5">Metered usage</h4>
                <p className="text-sm text-muted-foreground">
                  If you exceed your included resources, we'll charge a small hourly rate for
                  the additional resources. You'll see these charges clearly broken down in
                  your billing dashboard.
                </p>
              </div>
            </div>
          </section>

          <p className="text-center text-sm text-muted-foreground mt-8">
            Need a custom plan?{' '}
            <a
              href="mailto:sales@arpnetworking.com"
              className="text-primary hover:underline font-medium"
            >
              Contact our sales team
            </a>
          </p>
        </>
      ) : (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>No plans available at this time.</AlertDescription>
        </Alert>
      )}

      {selectedPlan ? (
        <UpgradePlanModal
          isOpen={isUpgradeModalOpen}
          onClose={handleCloseModal}
          plan={selectedPlan}
        />
      ) : null}
    </PageContainer>
  );
}

interface PlanIncludeProps {
  included: number;
  limit: number;
  noun: string;
  nounPlural: string;
  showLimit: boolean;
  /** Current peak usage for this resource, if known — drives the fit icon. */
  usage?: number;
}

/** One bullet row inside a plan card. Hoisted for readability. */
function PlanInclude({ included, limit, noun, nounPlural, showLimit, usage }: PlanIncludeProps) {
  const label = included === 1 ? noun : nounPlural;
  const status = fitStatus(usage, limit);
  return (
    <li className="flex items-start gap-2">
      <Check className="w-4 h-4 text-sev-4 flex-shrink-0 mt-0.5" />
      <span className="flex-1">
        <strong>{included}</strong> {label} included
        {showLimit ? (
          <span className="text-muted-foreground text-xs ml-1">(up to {limit})</span>
        ) : null}
      </span>
      {status !== 'ok' && usage !== undefined ? (
        <FitIndicator status={status} usage={usage} limit={limit} nounPlural={nounPlural} />
      ) : null}
    </li>
  );
}

interface FitIndicatorProps {
  status: Exclude<FitStatus, 'ok'>;
  usage: number;
  limit: number;
  nounPlural: string;
}

/**
 * The per-attribute warning icon + popover. Click (works on touch, unlike a
 * hover tooltip) reveals exactly how the current usage compares to this
 * plan's limit for the given resource.
 */
function FitIndicator({ status, usage, limit, nounPlural }: FitIndicatorProps) {
  const Icon = status === 'over' ? AlertCircle : AlertTriangle;
  const pct = limit > 0 ? Math.round((usage / limit) * 100) : 0;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            'flex-shrink-0 mt-0.5 rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            status === 'over' ? 'text-sev-2' : 'text-sev-3',
          )}
          aria-label={`${nounPlural} usage ${status === 'over' ? 'exceeds' : 'near'} this plan's limit`}
        >
          <Icon className="w-4 h-4" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-64 text-sm">
        <p className="font-medium mb-1">
          {status === 'over'
            ? `Exceeds this plan's ${nounPlural} limit`
            : `Near this plan's ${nounPlural} limit`}
        </p>
        <p className="text-muted-foreground text-xs">
          You're using <strong className="text-foreground">{usage}</strong> of {limit} {nounPlural}{' '}
          ({pct}%).{' '}
          {status === 'over'
            ? "This plan can't accommodate your current usage."
            : 'Limited headroom on this plan.'}
        </p>
      </PopoverContent>
    </Popover>
  );
}
